import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'
import { parseTime24 } from './time24'

/** Garde la saisie en cours tant que la valeur reçue est celle qu'on vient d'émettre. */
function useDraft(value: string, onValueChange: (value: string) => void) {
  const [draft, setDraft] = useState(value)
  const emittedRef = useRef(value)
  useEffect(() => {
    if (value !== emittedRef.current) {
      emittedRef.current = value
      setDraft(value)
    }
  }, [value])
  function emit(next: string) {
    emittedRef.current = next
    onValueChange(next)
  }
  return { draft, setDraft, emit }
}

interface FieldFrameProps {
  id: string
  label?: string | undefined
  hint?: string | undefined
  error?: string | undefined
  children: (messageId: string | undefined) => ReactNode
}

function FieldFrame({ id, label, hint, error, children }: FieldFrameProps) {
  const messageId = hint || error ? `${id}-message` : undefined
  if (!label) return <>{children(undefined)}</>
  return (
    <div className="ds-field">
      <label className="ds-field__label" htmlFor={id}>
        {label}
      </label>
      {children(messageId)}
      {error ? (
        <span id={messageId} className="ds-field__message ds-field__message--error">
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className="ds-field__message">
          {hint}
        </span>
      ) : null}
    </div>
  )
}

interface TimeFieldProps {
  value: string
  onValueChange: (value: string) => void
  label?: string | undefined
  /** Nom accessible quand le champ n'a pas d'étiquette visible. */
  ariaLabel?: string | undefined
  hint?: string | undefined
  error?: string | undefined
  required?: boolean | undefined
  disabled?: boolean | undefined
  className?: string | undefined
}

export function TimeInput({
  value,
  onValueChange,
  label,
  ariaLabel,
  hint,
  error,
  required,
  disabled,
  className,
}: TimeFieldProps) {
  const id = useId()
  const { draft, setDraft, emit } = useDraft(value, onValueChange)
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error}>
      {(messageId) => (
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="HH:MM"
          maxLength={5}
          className={mergeClassNames(
            label && 'ds-input',
            label && error && 'ds-input--error',
            'time-input-24',
            className,
          )}
          aria-label={label ? undefined : ariaLabel}
          aria-describedby={messageId}
          aria-invalid={Boolean(error)}
          required={required}
          disabled={disabled}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value)
            emit(parseTime24(event.target.value) ?? '')
          }}
          onBlur={() => {
            const parsed = parseTime24(draft)
            if (parsed) setDraft(parsed)
          }}
        />
      )}
    </FieldFrame>
  )
}

export function DateTimeInput({
  value,
  onValueChange,
  label,
  ariaLabel,
  hint,
  error,
  required,
  disabled,
  className,
}: TimeFieldProps) {
  const id = useId()
  const { draft, setDraft, emit } = useDraft(value, onValueChange)
  const [datePart = '', timePart = ''] = draft.split('T')
  const name = label ?? ariaLabel ?? 'Date'

  function update(date: string, time: string) {
    setDraft(`${date}T${time}`)
    const parsed = parseTime24(time)
    emit(date && parsed ? `${date}T${parsed}` : '')
  }

  return (
    <FieldFrame id={id} label={label} hint={hint} error={error}>
      {(messageId) => (
        <span className={mergeClassNames('datetime-input-24', className)}>
          <input
            id={id}
            type="date"
            className={mergeClassNames(label && 'ds-input', label && error && 'ds-input--error')}
            aria-label={label ? undefined : `${name} (date)`}
            aria-describedby={messageId}
            aria-invalid={Boolean(error)}
            required={required}
            disabled={disabled}
            value={datePart}
            onChange={(event) => {
              update(event.target.value, timePart)
            }}
          />
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="HH:MM"
            maxLength={5}
            className={mergeClassNames(
              label && 'ds-input',
              label && error && 'ds-input--error',
              'time-input-24',
            )}
            aria-label={`${name} (heure, 24 h)`}
            aria-describedby={messageId}
            aria-invalid={Boolean(error)}
            required={required}
            disabled={disabled}
            value={timePart}
            onChange={(event) => {
              update(datePart, event.target.value)
            }}
            onBlur={() => {
              const parsed = parseTime24(timePart)
              if (parsed) setDraft(`${datePart}T${parsed}`)
            }}
          />
        </span>
      )}
    </FieldFrame>
  )
}
